import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as editCustomerSelectors from '@makemydeal/dr-dash-store/selectors/editCustomerSelectors';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import type { BusinessEntity } from '@makemydeal/dr-dash-store/reducers/editCustomerReducer';
import BusinessSearchUI from './BusinessSearchUI';

interface BusinessSearchProps {
    handleNewBusiness: (searchText: string) => void;
}

const BusinessSearch: FC<BusinessSearchProps> = ({ handleNewBusiness }) => {
    const dispatch = useDispatch();
    const [searchValue, setSearchValue] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedBusinessEntityId, setSelectedBusinessEntityId] = useState<string | null>(null);

    const results = useSelector(editCustomerSelectors.getBusinessSearchResults);
    const loading = useSelector(editCustomerSelectors.getBusinessSearchLoading);
    const error = useSelector(editCustomerSelectors.getBusinessSearchError);

    const handleSearchValueChange = (value: string) => {
        setSearchValue(value);
    };

    const handleSearchClick = () => {
        const trimmed = searchValue.trim();
        if (trimmed) {
            setHasSearched(true);
            setSelectedBusinessEntityId(null);
            dispatch(editCustomerActionCreators.selectBusinessResult(''));
            dispatch(editCustomerActionCreators.searchBusinessByName(trimmed));
        }
    };

    const handleClear = () => {
        setSearchValue('');
        setHasSearched(false);
        setSelectedBusinessEntityId(null);
        dispatch(editCustomerActionCreators.selectBusinessResult(''));
        dispatch(editCustomerActionCreators.clearBusinessSearchResults());
    };

    const handleResultSelect = (entity: BusinessEntity) => {
        setSelectedBusinessEntityId(entity.businessEntityId);
        dispatch(editCustomerActionCreators.selectBusinessResult(entity.businessEntityId));
    };

    const handleNewBusinessClick = useCallback(() => {
        handleNewBusiness(searchValue);
    }, [handleNewBusiness, searchValue]);

    return (
        <BusinessSearchUI
            searchValue={searchValue}
            onSearchValueChange={handleSearchValueChange}
            onSearchClick={handleSearchClick}
            onClear={handleClear}
            results={results}
            loading={loading}
            error={error}
            hasSearched={hasSearched}
            selectedBusinessEntityId={selectedBusinessEntityId}
            onResultSelect={handleResultSelect}
            handleNewBusiness={handleNewBusinessClick}
        />
    );
};

export default BusinessSearch;
